// Batch-create script-generation requests from selected ads on the Fetched
// Ads page. One SAVED CI_STORYBOARDS row per selected ad, all targeting the
// same product. "Save & generate" additionally kicks off generation in the
// background — the rows progress in the Scripts tab.
//
// Defaults mirror Product Mapping's initial-state StoryboardConfig: the
// selected ad as the sole template, target duration from the ad's own
// video length (fallback 30s), all brief sections included, and creative
// direction left null so the LLM chooses based on the template.

import React, { useState, useEffect, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import VideoCreativeService from 'services/VideoCreativeService';
import {
  Product, ScrapedAd, StoryboardConfig, MarketingBriefSection,
} from './types';
import { defaultPatternsForAd, ALL_FEATURE_SUMMARY_KEYS } from './ProductMapping';

const service = new VideoCreativeService();

const ALL_BRIEF_SECTIONS: MarketingBriefSection[] = [
  'positioning', 'audience', 'benefits', 'featuresAndClaims', 'proof',
];

function buildDefaultConfig(ad: ScrapedAd): StoryboardConfig {
  return {
    templateAdIds: [ad.adId],
    targetDurationSec: ad.videoLengthSec ?? 30,
    constraints: '',
    templateElementMap: {},
    includeProductBrief: true,
    marketingBriefSections: ALL_BRIEF_SECTIONS,
    outputFormats: ['storyboard', 'flatScript'],
    hookDevice: null,
    narrativeStructure: null,
    productionFormat: null,
    tone: null,
    proofType: null,
    ctaType: null,
    // Custom-mode parity with Product Mapping's deep path: patterns are
    // prefilled from the ad's (flattened) features. The list rows here don't
    // carry the summary *texts* (those live on the CI_AD_FEATURES detail),
    // so we send all seven summary keys and let the backend include the
    // populated ones — same net prompt as the deep path's default.
    // templateSegments is omitted = "all segments", matching the deep path.
    templatePatterns: { [ad.adId]: defaultPatternsForAd(ad) },
    templateFeatureSummaries: { [ad.adId]: ALL_FEATURE_SUMMARY_KEYS },
  };
}

interface BatchScriptRequestDialogProps {
  open: boolean;
  selectedAds: ScrapedAd[];
  onClose: () => void;
  onSuccess: (opts: { count: number; generated: boolean; adsNeedingPipeline: number; adsNeedingPipelineIds: string[] }) => void;
}

export default function BatchScriptRequestDialog({
  open, selectedAds, onClose, onSuccess,
}: BatchScriptRequestDialogProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [submitting, setSubmitting] = useState<'save' | 'generate' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoadingProducts(true);
    setError(null);
    (async () => {
      // Same source Product Mapping uses — only products with a marketing
      // brief, since generation requires one.
      const result = await service.listProducts(undefined, { hasMarketingBrief: true });
      if (result?.products) setProducts(result.products);
      setLoadingProducts(false);
    })();
  }, [open]);

  // Un-extracted ads are handled automatically by the chaining flow on
  // "Save & generate" — the pipeline runs first, then script generation
  // kicks off via the extraction-completed listener. "Save requests"
  // alone doesn't trigger a pipeline run.
  const notReadyCount = useMemo(
    () => selectedAds.filter(a => a.extractionStatus !== 'DONE').length,
    [selectedAds],
  );

  const submit = async (generate: boolean) => {
    if (!product) return;
    setSubmitting(generate ? 'generate' : 'save');
    setError(null);
    const configs = selectedAds.map(buildDefaultConfig);
    const result = await service.saveStoryboardRequestsBatch(product.id, configs, { generate });
    setSubmitting(null);
    if (result?.storyboardIds) {
      const needIds = result.adsNeedingPipeline || [];
      onSuccess({
        count: result.storyboardIds.length,
        generated: generate,
        adsNeedingPipeline: needIds.length,
        adsNeedingPipelineIds: needIds,
      });
      setProduct(null);
    } else {
      setError('Failed to create script requests.');
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setProduct(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create script requests ({selectedAds.length})</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One SAVED request will be created per selected ad, all targeting the product you pick.
          Defaults match the Script Builder's initial config — the LLM chooses creative direction,
          and all brief sections are included.
        </Typography>

        <Autocomplete<Product, false, false, false>
          options={products}
          value={product}
          onChange={(_, next) => setProduct(next)}
          getOptionLabel={p => `${p.title}${p.store ? ` (${p.store})` : ''}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          loading={loadingProducts}
          renderInput={params => (
            <TextField
              {...params}
              label="Target product"
              placeholder="Pick a product…"
              required
            />
          )}
          sx={{ mb: 2 }}
        />

        {product && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2">
              <b>{selectedAds.length}</b> request{selectedAds.length === 1 ? '' : 's'} → <b>{product.title}</b>
            </Typography>
          </Box>
        )}

        {notReadyCount > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {notReadyCount} of {selectedAds.length} selected{' '}
            ad{notReadyCount === 1 ? ' isn' : 's aren'}'t analyzed yet.
            {' '}If you <b>Save&nbsp;&amp;&nbsp;generate</b>, the pipeline
            (transcribe&nbsp;→&nbsp;analyze&nbsp;→&nbsp;extract) will run for
            {notReadyCount === 1 ? ' it' : ' them'} first and script generation
            kicks off automatically once {notReadyCount === 1 ? "it's" : "they're"} ready.
            {' '}"Save requests" alone won't trigger the pipeline.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={!!submitting}>Cancel</Button>
        <Button
          onClick={() => submit(false)}
          disabled={!product || !!submitting}>
          {submitting === 'save'
            ? <><CircularProgress size={14} sx={{ mr: 1 }} /> Saving…</>
            : 'Save requests'}
        </Button>
        <Button
          variant="contained"
          onClick={() => submit(true)}
          disabled={!product || !!submitting}>
          {submitting === 'generate'
            ? <><CircularProgress size={14} sx={{ mr: 1 }} /> Submitting…</>
            : 'Save & generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
